/**
 * Generated by orval v8.23.0 🍺
 * Do not edit manually.
 * Rossum Agent API
 * AI agent for Rossum document processing. Debug hooks, deploy configs, and automate workflows conversationally.
 * OpenAPI spec version: 2.2.0dev0
 */
import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios';

/**
 * A suggested value for a slash command argument.
 */
export interface ArgumentSuggestion {
  value: string;
  description: string;
}

export type MessageRole = typeof MessageRole[keyof typeof MessageRole];


export const MessageRole = {
  user: 'user',
  assistant: 'assistant',
} as const;

/**
 * Text content in a message.
 */
export interface TextContent {
  type?: 'text';
  text: string;
}

export type ImageContentMediaType = typeof ImageContentMediaType[keyof typeof ImageContentMediaType];


export const ImageContentMediaType = {
  'image/jpeg': 'image/jpeg',
  'image/png': 'image/png',
  'image/gif': 'image/gif',
  'image/webp': 'image/webp',
} as const;

/**
 * Image content in a message.
 */
export interface ImageContent {
  type?: 'image';
  media_type: ImageContentMediaType;
  /** Base64-encoded image data */
  data: string;
}

/**
 * A single chat message.
 */
export interface Message {
  role: MessageRole;
  content: string | (TextContent | ImageContent)[];
  feedback?: boolean | null;
}

/**
 * Information about a file in a chat session.
 */
export interface FileInfo {
  filename: string;
  size: number;
  timestamp: string;
  mime_type?: string | null;
}

/**
 * Detailed chat session information.
 */
export interface ChatDetail {
  chat_id: string;
  messages: Message[];
  created_at: string;
  files: FileInfo[];
}

/**
 * Summary of a chat session for list responses.
 */
export interface ChatSummary {
  chat_id: string;
  timestamp: number;
  message_count: number;
  first_message: string;
  preview?: string | null;
  summary?: string | null;
  total_input_tokens?: number;
  total_output_tokens?: number;
  total_steps?: number;
}

/**
 * Response for listing chat sessions.
 */
export interface ChatListResponse {
  chats: ChatSummary[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Response for chat creation.
 */
export interface ChatResponse {
  chat_id: string;
  created_at: string;
}

/**
 * Information about an available slash command.
 */
export interface CommandInfo {
  name: string;
  description: string;
  argument_suggestions?: ArgumentSuggestion[];
}

/**
 * Response for listing available slash commands.
 */
export interface CommandListResponse {
  commands: CommandInfo[];
}

export type EntityChangeInfoOperation = typeof EntityChangeInfoOperation[keyof typeof EntityChangeInfoOperation];


export const EntityChangeInfoOperation = {
  create: 'create',
  update: 'update',
  delete: 'delete',
} as const;

/**
 * A single entity change within a config commit.
 */
export interface EntityChangeInfo {
  entity_type: string;
  entity_id: string;
  entity_name: string;
  operation: EntityChangeInfoOperation;
}

/**
 * A configuration commit with its changes.
 */
export interface CommitInfo {
  hash: string;
  timestamp: string;
  message: string;
  user_request: string;
  changes: EntityChangeInfo[];
}

/**
 * Response for listing config commits in a chat.
 */
export interface CommitListResponse {
  commits: CommitInfo[];
}

/**
 * Standard error response.
 */
export interface ErrorResponse {
  detail: string;
  error_code?: string | null;
}

export interface FeedbackRequest {
  /** @minimum 0 */
  turn_index: number;
  is_positive: boolean;
  /** Optional Rossum app URL for context (queue, etc.) */
  rossum_url?: string | null;
}

export interface FeedbackResponse {
  turn_index: number;
  is_positive: boolean;
}

export type ValidationErrorCtx = { [key: string]: unknown };

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: ValidationErrorCtx;
}

export interface HTTPValidationError {
  detail?: ValidationError[];
}

export type HealthResponseStatus = typeof HealthResponseStatus[keyof typeof HealthResponseStatus];


export const HealthResponseStatus = {
  healthy: 'healthy',
  unhealthy: 'unhealthy',
  shutting_down: 'shutting_down',
} as const;

/**
 * Response for health check endpoint.
 */
export interface HealthResponse {
  status: HealthResponseStatus;
  storage_connected: boolean;
  storage_backend: string;
  version: string;
}

export interface ReportToSlackRequest {
  /** Optional Rossum app URL for context (queue, etc.) */
  rossum_url?: string | null;
}

export interface ReportToSlackResponse {
  chat_id: string;
  channel: string;
  slack_ts: string;
}

/**
 * A single option for an agent question.
 */
export interface QuestionOptionSchema {
  value: string;
  label: string;
  description?: string;
}

/**
 * A single question within an agent question event.
 */
export interface AgentQuestionItemSchema {
  question: string;
  options?: QuestionOptionSchema[];
  multi_select?: boolean;
}

export type TaskSnapshotTaskSchemaStatus = typeof TaskSnapshotTaskSchemaStatus[keyof typeof TaskSnapshotTaskSchemaStatus];


export const TaskSnapshotTaskSchemaStatus = {
  pending: 'pending',
  in_progress: 'in_progress',
  completed: 'completed',
} as const;

/**
 * A single task within a task snapshot event.
 */
export interface TaskSnapshotTaskSchema {
  id: string;
  subject: string;
  status: TaskSnapshotTaskSchemaStatus;
  description?: string;
}

/**
 * Final answer payload emitted as a data-final-answer wire event.
 */
export interface FinalAnswerSchema {
  text: string;
}

/**
 * A file created during an agent run.
 */
export interface FileCreatedSchema {
  filename: string;
  url: string;
}

export type DocumentContentMediaType = typeof DocumentContentMediaType[keyof typeof DocumentContentMediaType];


export const DocumentContentMediaType = {
  'application/pdf': 'application/pdf',
  'text/csv': 'text/csv',
  'text/plain': 'text/plain',
  'text/markdown': 'text/markdown',
  'application/vndopenxmlformats-officedocumentspreadsheetmlsheet': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vndms-excel': 'application/vnd.ms-excel',
} as const;

/**
 * Document content in a message.
 */
export interface DocumentContent {
  type?: 'document';
  media_type: DocumentContentMediaType;
  /** Base64-encoded document data */
  data: string;
  /** Original filename of the document */
  filename: string;
}

export type Persona = typeof Persona[keyof typeof Persona];


export const Persona = {
  default: 'default',
  cautious: 'cautious',
} as const;

/**
 * MCP mode to use for this message and all subsequent messages. If not specified, uses the chat's current mode.
 */
export type MessageRequestMcpMode = typeof MessageRequestMcpMode[keyof typeof MessageRequestMcpMode] | null;


export const MessageRequestMcpMode = {
  'read-only': 'read-only',
  'read-write': 'read-write',
} as const;

/**
 * Request body for sending a message.
 *
 * Supports text-only messages or multimodal messages with images and documents.
 * For image messages, use the `images` field with base64-encoded image data.
 * For document messages, use the `documents` field with base64-encoded data (PDF, CSV, Excel, plain text).
 */
export interface MessageRequest {
  /**
     * Text content of the message
     * @minLength 1
     * @maxLength 50000
     */
  content: string;
  /** Optional list of images (max 5) to include with the message */
  images?: ImageContent[] | null;
  /** Optional list of documents (max 5) to include with the message. Supported formats: PDF, CSV, Excel (.xlsx, .xls), plain text. */
  documents?: DocumentContent[] | null;
  /** Optional Rossum app URL for context */
  rossum_url?: string | null;
  /** MCP mode to use for this message and all subsequent messages. If not specified, uses the chat's current mode. */
  mcp_mode?: MessageRequestMcpMode;
  /** Agent persona to use for this message and all subsequent messages. If not specified, uses the chat's current persona. */
  persona?: Persona | null;
}

export type ListChatsApiV1ChatsGetParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export const getRossumAgentAPI = (axiosInstance: AxiosInstance = axios) => {
/**
 * Check API health and dependencies.
 * @summary Health Check
 */
const healthCheckApiV1HealthGet = (
     options?: AxiosRequestConfig
 ): Promise<AxiosResponse<HealthResponse>> => {
    return axiosInstance.get(
      `/api/v1/health`,options
    );
  }

/**
 * @summary Create Chat
 */
const createChatApiV1ChatsPost = (
     options?: AxiosRequestConfig
 ): Promise<AxiosResponse<ChatResponse>> => {
    return axiosInstance.post(
      `/api/v1/chats`,
      undefined,options
    );
  }

/**
 * List chat sessions for the authenticated user.
 * @summary List Chats
 */
const listChatsApiV1ChatsGet = (
    params?: ListChatsApiV1ChatsGetParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<ChatListResponse>> => {
    return axiosInstance.get(
      `/api/v1/chats`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }

/**
 * Get detailed information about a chat session.
 * @summary Get Chat
 */
const getChatApiV1ChatsChatIdGet = (
    chatId: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<ChatDetail>> => {
    return axiosInstance.get(
      `/api/v1/chats/${chatId}`,options
    );
  }

/**
 * List configuration commits made in a chat session.
 * @summary List Chat Commits
 */
const listChatCommitsApiV1ChatsChatIdCommitsGet = (
    chatId: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CommitListResponse>> => {
    return axiosInstance.get(
      `/api/v1/chats/${chatId}/commits`,options
    );
  }

/**
 * @summary Submit Feedback
 */
const submitFeedbackApiV1ChatsChatIdFeedbackPut = (
    chatId: string,
    feedbackRequest: FeedbackRequest, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<FeedbackResponse>> => {
    return axiosInstance.put(
      `/api/v1/chats/${chatId}/feedback`,
      feedbackRequest,options
    );
  }

/**
 * @summary Send Message
 */
const sendMessageApiV1ChatsChatIdMessagesPost = (
    chatId: string,
    messageRequest: MessageRequest, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<unknown>> => {
    return axiosInstance.post(
      `/api/v1/chats/${chatId}/messages`,
      messageRequest,options
    );
  }

/**
 * @summary List Commands
 */
const listCommandsApiV1CommandsGet = (
     options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CommandListResponse>> => {
    return axiosInstance.get(
      `/api/v1/commands`,options
    );
  }

/**
 * Download a file from a chat session.
 * @summary Download File
 */
const downloadFileApiV1ChatsChatIdFilesFilenameGet = (
    chatId: string,
    filename: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<unknown>> => {
    return axiosInstance.get(
      `/api/v1/chats/${chatId}/files/${filename}`,options
    );
  }

/**
 * @summary Report To Slack
 */
const reportToSlackApiV1ChatsChatIdReportToSlackPost = (
    chatId: string,
    reportToSlackRequestNull?: ReportToSlackRequest | null, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<ReportToSlackResponse>> => {
    return axiosInstance.post(
      `/api/v1/chats/${chatId}/report-to-slack`,
      reportToSlackRequestNull,options
    );
  }

return {healthCheckApiV1HealthGet,createChatApiV1ChatsPost,listChatsApiV1ChatsGet,getChatApiV1ChatsChatIdGet,listChatCommitsApiV1ChatsChatIdCommitsGet,submitFeedbackApiV1ChatsChatIdFeedbackPut,sendMessageApiV1ChatsChatIdMessagesPost,listCommandsApiV1CommandsGet,downloadFileApiV1ChatsChatIdFilesFilenameGet,reportToSlackApiV1ChatsChatIdReportToSlackPost}};
export type HealthCheckApiV1HealthGetResult = AxiosResponse<HealthResponse>
export type CreateChatApiV1ChatsPostResult = AxiosResponse<ChatResponse>
export type ListChatsApiV1ChatsGetResult = AxiosResponse<ChatListResponse>
export type GetChatApiV1ChatsChatIdGetResult = AxiosResponse<ChatDetail>
export type ListChatCommitsApiV1ChatsChatIdCommitsGetResult = AxiosResponse<CommitListResponse>
export type SubmitFeedbackApiV1ChatsChatIdFeedbackPutResult = AxiosResponse<FeedbackResponse>
export type SendMessageApiV1ChatsChatIdMessagesPostResult = AxiosResponse<unknown>
export type ListCommandsApiV1CommandsGetResult = AxiosResponse<CommandListResponse>
export type DownloadFileApiV1ChatsChatIdFilesFilenameGetResult = AxiosResponse<unknown>
export type ReportToSlackApiV1ChatsChatIdReportToSlackPostResult = AxiosResponse<ReportToSlackResponse>
